// T104: Create CommentForm component

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/stores/authStore'
import { useCreateComment } from '@/hooks/useComments'
import { Loader2, Send } from 'lucide-react'
import { toast } from 'sonner'

interface CommentFormProps {
  parentAuthor: string
  parentPermlink: string
  rootAuthor?: string
  rootPermlink?: string
  placeholder?: string
  onSuccess?: () => void
  autoFocus?: boolean
  compact?: boolean
}

export function CommentForm({
  parentAuthor,
  parentPermlink,
  rootAuthor,
  rootPermlink,
  placeholder = '댓글을 작성하세요...',
  onSuccess,
  autoFocus = false,
  compact = false,
}: CommentFormProps) {
  const [body, setBody] = useState('')
  const { user, isAuthenticated } = useAuthStore()

  const createComment = useCreateComment({
    parentAuthor,
    parentPermlink,
    rootAuthor,
    rootPermlink,
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const trimmedBody = body.trim()

    // T113: Validate comment content
    if (!trimmedBody) {
      toast.error('댓글 내용을 입력해주세요')
      return
    }

    if (trimmedBody.length > 65535) {
      toast.error('댓글이 너무 깁니다 (최대 65535자)')
      return
    }

    try {
      await createComment.mutateAsync(trimmedBody)
      setBody('')
      toast.success('댓글이 작성되었습니다')
      onSuccess?.()
    } catch (error) {
      // T114: Handle comment submission errors
      toast.error('댓글 작성 실패', {
        description: error instanceof Error ? error.message : '알 수 없는 오류',
      })
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <p>댓글을 작성하려면 로그인해주세요</p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className={compact ? 'mt-2' : 'mt-6'}>
      <div className="flex gap-3">
        {!compact && (
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarImage
              src={`https://steemitimages.com/u/${user?.username}/avatar`}
              alt={user?.username}
            />
            <AvatarFallback className="text-xs">
              {user?.username?.[0]?.toUpperCase()}
            </AvatarFallback>
          </Avatar>
        )}

        <div className="flex-1 space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            disabled={createComment.isPending}
            className={compact ? 'min-h-[60px]' : 'min-h-[80px]'}
            rows={compact ? 2 : 3}
          />

          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {body.length > 0 && `${body.length.toLocaleString()}자`}
            </span>
            <Button
              type="submit"
              size={compact ? 'sm' : 'default'}
              disabled={!body.trim() || createComment.isPending}
            >
              {createComment.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  작성 중...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  댓글 작성
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}

interface ReplyFormProps {
  parentAuthor: string
  parentPermlink: string
  rootAuthor: string
  rootPermlink: string
  onSuccess?: () => void
  onCancel?: () => void
}

export function ReplyForm({
  parentAuthor,
  parentPermlink,
  rootAuthor,
  rootPermlink,
  onSuccess,
  onCancel,
}: ReplyFormProps) {
  const [body, setBody] = useState('')
  const { user, isAuthenticated } = useAuthStore()

  const createComment = useCreateComment({
    parentAuthor,
    parentPermlink,
    rootAuthor,
    rootPermlink,
  })

  const submitReply = async () => {
    const trimmedBody = body.trim()

    if (!trimmedBody) {
      toast.error('답글 내용을 입력해주세요')
      return
    }

    if (createComment.isPending) {
      return
    }

    try {
      await createComment.mutateAsync(trimmedBody)
      setBody('')
      toast.success('답글이 작성되었습니다')
      onSuccess?.()
    } catch (error) {
      toast.error('답글 작성 실패', {
        description: error instanceof Error ? error.message : '알 수 없는 오류',
      })
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    submitReply()
  }

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault()
    submitReply()
  }

  if (!isAuthenticated) {
    return null
  }

  return (
    <form onSubmit={handleSubmit} className="mt-3 pl-11">
      <div className="flex gap-2">
        <Avatar className="h-6 w-6 flex-shrink-0">
          <AvatarImage
            src={`https://steemitimages.com/u/${user?.username}/avatar`}
            alt={user?.username}
          />
          <AvatarFallback className="text-xs">
            {user?.username?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={`@${parentAuthor}에게 답글...`}
            autoFocus
            disabled={createComment.isPending}
            className="min-h-[50px] text-sm"
            rows={2}
          />

          <div className="flex items-center gap-2 justify-end">
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onCancel}
                disabled={createComment.isPending}
              >
                취소
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={!body.trim() || createComment.isPending}
              onClick={handleButtonClick}
            >
              {createComment.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                '답글'
              )}
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
